import type { ReactElement } from 'react';
import { useNavigate } from 'react-router';
import type { DifficultyLevel, DocumentKind, SearchResultRow } from '@xdocs/schemas';
import { Button, EmptyState, Input, Select, SearchInput, Skeleton, cn, useI18n } from '@xdocs/ui';
import {
  CategoryFilterChips,
  DEFAULT_SEARCH_FILTERS,
  SearchResultCard,
  useCategoryCatalog,
  useSearchQuery,
  useSearchT,
} from '../../features/search/index.js';
import styles from './SearchPage.module.css';

const SKELETON_CARD_COUNT = 4;

/**
 * `/search` (F8, F30, F31). Debounced + min-chars-guarded query, F31
 * category/kind/difficulty/language filters, sort, "Load more" pagination
 * (mobile rule 0.7 — always append here, no numbered-pager variant since
 * this isn't a `<DataTable>`), and a per-result F30 "never show again"
 * action with an optimistic remove + Undo toast. Three distinct empty
 * kinds (E19/ux.md): first-use (nothing searched yet), no-results (names
 * the active query + Clear filters), error (retry).
 */
export function SearchPage(): ReactElement {
  const navigate = useNavigate();
  const { t: tGlobal } = useI18n();
  const { t } = useSearchT();
  const {
    qDraft,
    setQDraft,
    isBelowMinChars,
    hasSearched,
    filters,
    setFilters,
    sort,
    setSort,
    dir,
    rows,
    total,
    hasMore,
    isLoading,
    isLoadingMore,
    isError,
    loadMoreError,
    onRetry,
    onLoadMore,
    suppress,
    hidingShareId,
  } = useSearchQuery();

  // Source of truth for the filter chips is the FULL catalog (§5.9), never
  // the currently-loaded `search.query` page — see `useCategoryCatalog`'s
  // doc comment for why deriving from `rows` self-eliminates the facet.
  const { categories } = useCategoryCatalog();

  function onOpen(result: SearchResultRow): void {
    void navigate(`/discover/${result.shareId}`);
  }

  const isEmpty = !isLoading && !isError && rows.length === 0;
  const isNoResults = isEmpty && hasSearched && !isBelowMinChars;
  const isFirstUseEmpty = isEmpty && !hasSearched;

  return (
    <div className={cn(styles['page'])}>
      <h1 className={cn(styles['title'])}>{t('search:page.title')}</h1>

      <div className={cn(styles['searchRow'])}>
        <SearchInput
          label={t('search:input.label')}
          placeholder={t('search:input.placeholder')}
          value={qDraft}
          onChange={(event) => setQDraft(event.target.value)}
          onClear={() => setQDraft('')}
          clearLabel={t('search:input.clear')}
          fullWidth
          containerClassName={styles['searchField']}
          className={styles['embeddedInput']}
        />
        {hasSearched && total !== null && (
          <span className={cn(styles['resultsCount'])}>{t('search:input.resultsCount', { count: total })}</span>
        )}
      </div>
      {isBelowMinChars && <p className={cn(styles['hint'])}>{t('search:hint.belowMinChars')}</p>}

      <div className={cn(styles['filtersRow'])}>
        <div className={cn(styles['filterField'])}>
          <Select
            label={t('search:filters.kind.label')}
            value={filters.kind ?? ''}
            onChange={(event) =>
              setFilters({ ...filters, kind: event.target.value === '' ? null : (event.target.value as DocumentKind) })
            }
            options={[
              { value: '', label: t('search:filters.kind.any') },
              { value: 'book', label: t('search:filters.kind.book') },
              { value: 'article', label: t('search:filters.kind.article') },
              { value: 'report', label: t('search:filters.kind.report') },
            ]}
          />
        </div>
        <div className={cn(styles['filterField'])}>
          <Select
            label={t('search:filters.difficulty.label')}
            value={filters.difficultyLevel ?? ''}
            onChange={(event) =>
              setFilters({
                ...filters,
                difficultyLevel: event.target.value === '' ? null : (event.target.value as DifficultyLevel),
              })
            }
            options={[
              { value: '', label: t('search:filters.difficulty.any') },
              { value: 'basic', label: t('search:filters.difficulty.basic') },
              { value: 'intermediate', label: t('search:filters.difficulty.intermediate') },
              { value: 'advanced', label: t('search:filters.difficulty.advanced') },
              { value: 'mastery', label: t('search:filters.difficulty.mastery') },
            ]}
          />
        </div>
        <div className={cn(styles['filterField'])}>
          <Input
            label={t('search:filters.language.label')}
            placeholder={t('search:filters.language.placeholder')}
            value={filters.language}
            onChange={(event) => setFilters({ ...filters, language: event.target.value })}
          />
        </div>
        <div className={cn(styles['filterField'])}>
          <Select
            label={t('search:filters.sort.label')}
            value={sort}
            onChange={(event) => setSort(event.target.value as typeof sort, dir)}
            options={[
              { value: 'relevance', label: t('search:filters.sort.relevance') },
              { value: 'updatedAt', label: t('search:filters.sort.updatedAt') },
            ]}
          />
        </div>
      </div>

      <CategoryFilterChips
        categories={categories}
        activeCategoryId={filters.categoryId}
        onSelect={(categoryId) => setFilters({ ...filters, categoryId })}
      />

      {isError && (
        <EmptyState kind="error" title={t('search:empty.error.title')} action={{ label: tGlobal('common:action.retry'), onClick: onRetry }} />
      )}

      {!isError && isFirstUseEmpty && (
        <EmptyState kind="first-use" title={t('search:empty.firstUse.title')} description={t('search:empty.firstUse.description')} />
      )}

      {!isError && isNoResults && (
        <EmptyState
          kind="no-results"
          title={t('search:empty.noResults.title', { query: qDraft })}
          description={t('search:empty.noResults.description')}
          action={{ label: tGlobal('common:action.clearFilters'), onClick: () => setFilters(DEFAULT_SEARCH_FILTERS) }}
        />
      )}

      {isLoading && rows.length === 0 && (
        <div className={cn(styles['resultsList'])} aria-hidden="true">
          <Skeleton shape="row" count={SKELETON_CARD_COUNT} />
        </div>
      )}

      {!isError && rows.length > 0 && (
        <ul className={cn(styles['resultsList'])} aria-label={t('search:page.title')}>
          {rows.map((row) => (
            <li key={row.shareId}>
              <SearchResultCard result={row} onOpen={onOpen} onHide={suppress} isHiding={hidingShareId === row.shareId} />
            </li>
          ))}
        </ul>
      )}

      {!isError && rows.length > 0 && (
        <div className={cn(styles['loadMore'])}>
          {loadMoreError && <p role="alert" className={cn(styles['loadMoreError'])}>{loadMoreError}</p>}
          {!loadMoreError && hasMore && (
            <Button variant="secondary" state={isLoadingMore ? 'loading' : 'idle'} onClick={onLoadMore}>
              {tGlobal('common:action.loadMore')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
